/**
 * @file Inbox.tsx
 * @description "Inbox" — quick-capture list for the personal capture pipeline (POST /api/capture
 * → inbox table → async AI classification). Renders one card per captured entry showing the raw
 * text and/or image thumbnail, the classification status (new = gray, pending = amber,
 * classified = emerald with type + domain + due date), and three actions: inline manual
 * correction (PATCH with fields), re-run AI classification (PATCH {action:"reclassify"}), and
 * dismiss (PATCH {action:"dismiss"}). Classified task-type items additionally offer
 * "→ Zadanie" (PATCH {action:"promote-to-task"}) which creates a life-layer task with an
 * inline prefilled form. Data is polled every 30 seconds like Infrastructure.tsx.
 * All page copy is intentionally Polish (see the inbox i18n namespace) since Polish is the
 * deliberate language of this instance.
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Ban,
  Calendar,
  ClipboardCopy,
  Check,
  Clock,
  Inbox as InboxIcon,
  Lightbulb,
  ListTodo,
  Pencil,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { api, INBOX_DOMAINS, INBOX_TYPES } from "../lib/api";
import type { InboxItem, InboxItemType, InboxPromoteFields, InboxUpdateFields } from "../lib/api";
import { eventBus } from "../lib/eventBus";
import { EmptyState } from "../components/EmptyState";
import { formatDateTime, timeAgo } from "../lib/format";

const POLL_INTERVAL_MS = 30_000;

const STATUS_STYLES: Record<
  InboxItem["status"],
  { dot: string; badge: string; labelKey: string; pulse: boolean }
> = {
  new: {
    dot: "bg-gray-400",
    badge: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    labelKey: "statusNew",
    pulse: false,
  },
  pending: {
    dot: "bg-amber-400",
    badge: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    labelKey: "statusPending",
    pulse: true,
  },
  classified: {
    dot: "bg-emerald-400",
    badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    labelKey: "statusClassified",
    pulse: false,
  },
  dismissed: {
    dot: "bg-gray-600",
    badge: "bg-gray-500/10 text-gray-500 border-gray-500/20",
    labelKey: "statusDismissed",
    pulse: false,
  },
};

interface EditDraft {
  type: InboxItemType;
  domain: string;
  title: string;
  dueAt: string; // datetime-local value ("" = none)
}

/** ISO timestamp → value for <input type="datetime-local"> in local time. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO string (or null when cleared). */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Prefill for the inline promote-to-task form (classified task items). */
interface PromoteDraft {
  title: string;
  domain: string;
  dueAt: string; // datetime-local value ("" = none)
}

function InboxCard({
  item,
  busy,
  onUpdate,
  onReclassify,
  onDismiss,
  onPromote,
}: {
  item: InboxItem;
  busy: boolean;
  onUpdate: (id: string, fields: InboxUpdateFields) => Promise<void>;
  onReclassify: (id: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  onPromote: (id: string, fields: InboxPromoteFields) => Promise<void>;
}) {
  const { t } = useTranslation("inbox");

  // Sklada gotowy prompt ze sprawa i kontekstem. Marcin wkleja go do Claude Code
  // (split-screen) — dashboard pokazuje stan, Claude Code jest rekami.
  async function doClaudeCode() {
    const tekst = [
      `Sprawa z kolejki operacyjnej (${item.type || "bez typu"}${item.domain ? ` / ${item.domain}` : ""}):`,
      "",
      item.title || item.raw_text || "(bez tytulu)",
      item.details ? `\n${item.details}` : "",
      "",
      "Zajmij sie tym. Jesli to alert — zdiagnozuj przyczyne, zanim cokolwiek naprawisz.",
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(tekst);
      setSkopiowane(true);
      setTimeout(() => setSkopiowane(false), 2000);
    } catch {
      /* schowek zablokowany przez przegladarke — przycisk po prostu nie potwierdzi */
    }
  }

  // Zamkniecie sprawy z wnioskiem: oryginal znika z kolejki, wniosek zostaje jako
  // osobna pozycja do zrobienia. To jest cala petla — bez tego lekcja ginie.
  async function zapiszWniosek() {
    const tresc = (wniosek || "").trim();
    if (!tresc) return;
    await fetch("/api/inbox/ingest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        source: "petla-wnioskow",
        type: "idea",
        domain: item.domain || null,
        title: `Wniosek: ${tresc}`.slice(0, 120),
        details: `Wynikl z zamknietej sprawy: ${item.title || item.raw_text || ""}`.slice(0, 400),
        dedupe_key: `wniosek:${item.id}`,
        dedupe_window_h: 24 * 30,
      }),
    });
    setWniosek(null);
    await onDismiss(item.id);
  }

  const [editing, setEditing] = useState(false);
  // Most do Claude Code — sciezka 1 z ADR mostu: schowek. Zero integracji, zero
  // powierzchni bezpieczenstwa; Kokpit pokazuje sprawe, Claude Code ja wykonuje.
  const [skopiowane, setSkopiowane] = useState(false);
  // Petla „kazda sprawa ma ulepszyc firme" — zamkniecie sprawy zostawia wniosek,
  // ktory wraca do kolejki jako osobna pozycja. Inaczej lekcja ginie razem ze sprawa.
  const [wniosek, setWniosek] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [promoteDraft, setPromoteDraft] = useState<PromoteDraft | null>(null);
  const style = STATUS_STYLES[item.status] ?? STATUS_STYLES.new;

  const startEdit = () => {
    setDraft({
      type: item.type ?? "note",
      domain: item.domain ?? INBOX_DOMAINS[0],
      title: item.title ?? item.raw_text ?? "",
      dueAt: isoToLocalInput(item.due_at),
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!draft) return;
    await onUpdate(item.id, {
      type: draft.type,
      domain: draft.domain,
      title: draft.title,
      dueAt: localInputToIso(draft.dueAt),
    });
    setEditing(false);
    setDraft(null);
  };

  const startPromote = () => {
    setPromoteDraft({
      title: item.title ?? item.raw_text ?? "",
      domain: item.domain ?? "",
      dueAt: isoToLocalInput(item.due_at),
    });
  };

  const savePromote = async () => {
    if (!promoteDraft) return;
    await onPromote(item.id, {
      title: promoteDraft.title,
      domain: promoteDraft.domain || null,
      dueAt: localInputToIso(promoteDraft.dueAt),
    });
    setPromoteDraft(null);
  };

  return (
    <div className="card-hover p-4 space-y-3">
      {/* Header: status + timestamp + source */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 min-w-0">
          <span
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot} ${
              style.pulse ? "animate-pulse-dot" : ""
            }`}
            aria-hidden="true"
          />
          <span className={`badge ${style.badge}`}>{t(style.labelKey)}</span>
          {item.status === "classified" && item.type && (
            <span className="badge bg-accent/10 text-accent border-accent/20">
              {t(`typeNames.${item.type}`, { defaultValue: item.type })}
            </span>
          )}
          {item.status === "classified" && item.domain && (
            <span className="badge bg-surface-3 text-gray-300 border-border">{item.domain}</span>
          )}
        </div>
        <span
          className="text-[11px] text-gray-500 font-mono flex-shrink-0"
          title={formatDateTime(item.created_at)}
        >
          {timeAgo(item.created_at)}
          {item.source ? ` · ${item.source}` : ""}
        </span>
      </div>

      {/* Body: classified title + raw text + image thumbnail */}
      {item.status === "classified" && item.title && (
        <h3 className="text-sm font-medium text-gray-100">{item.title}</h3>
      )}
      {item.raw_text && (
        <p
          className={`text-xs whitespace-pre-wrap break-words ${
            item.status === "classified" && item.title ? "text-gray-500" : "text-gray-300"
          }`}
        >
          {item.raw_text}
        </p>
      )}
      {item.image_path && (
        <a href={api.inbox.imageUrl(item.id)} target="_blank" rel="noopener noreferrer">
          <img
            src={api.inbox.imageUrl(item.id)}
            alt={t("imageAlt")}
            loading="lazy"
            className="max-h-40 rounded-lg border border-border object-contain"
          />
        </a>
      )}

      {/* Classified extras: due / remind / details */}
      {item.status === "classified" && (item.due_at || item.remind_at || item.details) && (
        <div className="space-y-1 text-xs text-gray-400">
          {item.due_at && (
            <p className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-gray-500 flex-shrink-0" />
              {t("dueAt", { date: formatDateTime(item.due_at) })}
            </p>
          )}
          {item.remind_at && (
            <p className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-gray-500 flex-shrink-0" />
              {t("remindAt", { date: formatDateTime(item.remind_at) })}
            </p>
          )}
          {item.details && <p className="text-gray-500">{item.details}</p>}
        </div>
      )}

      {/* Classification error (pending) */}
      {item.status === "pending" && item.error && (
        <p className="flex items-center gap-1.5 text-xs text-amber-400">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{t("classifyError", { error: item.error })}</span>
        </p>
      )}

      {/* Inline edit form */}
      {editing && draft ? (
        <div className="space-y-2 rounded-lg border border-border bg-surface-2 p-3">
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("fieldType")}
              <select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value as InboxItemType })}
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
              >
                {INBOX_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`typeNames.${type}`, { defaultValue: type })}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("fieldDomain")}
              <select
                value={draft.domain}
                onChange={(e) => setDraft({ ...draft, domain: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
              >
                {INBOX_DOMAINS.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t("fieldTitle")}
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
            />
          </label>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t("fieldDueAt")}
            <input
              type="datetime-local"
              value={draft.dueAt}
              onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })}
              className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
            />
          </label>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={saveEdit}
              disabled={busy || !draft.title.trim()}
              className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <Check className="w-3.5 h-3.5" />
              {t("actionSave")}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraft(null);
              }}
              disabled={busy}
              className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <X className="w-3.5 h-3.5" />
              {t("actionCancel")}
            </button>
          </div>
        </div>
      ) : promoteDraft ? (
        /* Inline promote-to-task form — prefilled from the classified fields */
        <div className="space-y-2 rounded-lg border border-border bg-surface-2 p-3">
          <p className="text-[11px] font-semibold text-gray-300">{t("promoteTitle")}</p>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
            {t("fieldTitle")}
            <input
              type="text"
              value={promoteDraft.title}
              onChange={(e) => setPromoteDraft({ ...promoteDraft, title: e.target.value })}
              className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("fieldDomain")}
              <select
                value={promoteDraft.domain}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, domain: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
              >
                <option value="">—</option>
                {INBOX_DOMAINS.map((domain) => (
                  <option key={domain} value={domain}>
                    {domain}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              {t("fieldDueAt")}
              <input
                type="datetime-local"
                value={promoteDraft.dueAt}
                onChange={(e) => setPromoteDraft({ ...promoteDraft, dueAt: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-2 py-1.5 text-xs text-gray-200"
              />
            </label>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={savePromote}
              disabled={busy || !promoteDraft.title.trim()}
              className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <ListTodo className="w-3.5 h-3.5" />
              {t("actionCreateTask")}
            </button>
            <button
              onClick={() => setPromoteDraft(null)}
              disabled={busy}
              className="btn-ghost text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
            >
              <X className="w-3.5 h-3.5" />
              {t("actionCancel")}
            </button>
          </div>
        </div>
      ) : (
        /* Actions */
        <div className="flex items-center gap-2 pt-1">
          {item.status === "classified" && item.type === "task" && (
            <button
              onClick={startPromote}
              disabled={busy}
              className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60 text-emerald-400 hover:text-emerald-300"
            >
              <ListTodo className="w-3.5 h-3.5" />
              {t("actionPromote")}
            </button>
          )}
          <button
            onClick={startEdit}
            disabled={busy}
            className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Pencil className="w-3.5 h-3.5" />
            {t("actionEdit")}
          </button>
          <button
            onClick={() => onReclassify(item.id)}
            disabled={busy}
            className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t("actionReclassify")}
          </button>
          <button
            onClick={doClaudeCode}
            disabled={busy}
            className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            title="Kopiuje sprawę jako gotowy prompt — wklej do Claude Code"
          >
            <ClipboardCopy className="w-3.5 h-3.5" />
            {skopiowane ? "Skopiowane" : "Do Claude Code"}
          </button>
          <button
            onClick={() => setWniosek(wniosek === null ? "" : null)}
            disabled={busy}
            className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            title="Zamknij sprawę i zostaw wniosek, żeby się nie powtórzyła"
          >
            <Lightbulb className="w-3.5 h-3.5" />
            Wniosek
          </button>
          <button
            onClick={() => onDismiss(item.id)}
            disabled={busy}
            className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 text-gray-500 hover:text-red-400 disabled:opacity-60"
          >
            <Ban className="w-3.5 h-3.5" />
            {t("actionDismiss")}
          </button>
        </div>
      )}

      {wniosek !== null && (
        <div className="pt-2">
          <label htmlFor={`wniosek-${item.id}`} className="block text-xs text-gray-400 mb-1">
            Czego ta sprawa uczy? Wniosek wróci do kolejki jako osobna pozycja.
          </label>
          <textarea
            id={`wniosek-${item.id}`}
            value={wniosek}
            onChange={(e) => setWniosek(e.target.value)}
            rows={2}
            className="input w-full text-sm"
            placeholder="np. monitoring nie sprawdzał, czy kontener wstał po restarcie"
          />
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={zapiszWniosek}
              disabled={busy || !wniosek.trim()}
              className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 disabled:opacity-60"
            >
              <Check className="w-3.5 h-3.5" />
              Zamknij z wnioskiem
            </button>
            <button
              onClick={() => setWniosek(null)}
              disabled={busy}
              className="btn-ghost text-xs px-2.5 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <X className="w-3.5 h-3.5" />
              Anuluj
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Inbox() {
  const { t } = useTranslation("inbox");
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const wsConnected = useSyncExternalStore(eventBus.onConnection, () => eventBus.connected);

  const load = useCallback(async () => {
    try {
      const list = await api.inbox.list("all");
      // Dismissed entries are kept server-side for audit but hidden here.
      setItems(list.filter((x) => x.status !== "dismissed"));
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const withBusy = useCallback(
    async (id: string, action: () => Promise<unknown>) => {
      setBusyId(id);
      setActionError(null);
      try {
        await action();
        await load();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyId(null);
      }
    },
    [load]
  );

  const onUpdate = useCallback(
    (id: string, fields: InboxUpdateFields) => withBusy(id, () => api.inbox.update(id, fields)),
    [withBusy]
  );
  const onReclassify = useCallback(
    (id: string) => withBusy(id, () => api.inbox.reclassify(id)),
    [withBusy]
  );
  const onDismiss = useCallback(
    (id: string) => withBusy(id, () => api.inbox.dismiss(id)),
    [withBusy]
  );
  const onPromote = useCallback(
    (id: string, fields: InboxPromoteFields) =>
      withBusy(id, () => api.inbox.promoteToTask(id, fields)),
    [withBusy]
  );

  const newCount = items?.filter((x) => x.status === "new").length ?? 0;
  const pendingCount = items?.filter((x) => x.status === "pending").length ?? 0;

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <InboxIcon className="w-4.5 h-4.5 text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-100">{t("title")}</h1>
                {wsConnected ? (
                  <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                    {t("common:live")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 bg-gray-500/10 border border-gray-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    {t("common:offline")}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-2">
                {t("subtitle")}
                <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 bg-surface-2 border border-border px-2 py-0.5 rounded-md font-mono ml-2">
                  <Clock className="w-3 h-3" />
                  {lastUpdate.toLocaleTimeString()}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {items && items.length > 0 && (
            <span className="text-xs text-gray-500">
              {t("countSummary", { newCount, pendingCount })}
            </span>
          )}
          <button onClick={load} className="btn-ghost" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {t("common:refresh")}
          </button>
        </div>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="card p-3 flex items-center gap-3 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-xs text-gray-300 min-w-0 truncate">
            {t("actionError")}: <span className="font-mono text-gray-500">{actionError}</span>
          </p>
        </div>
      )}

      {/* Error state (endpoint unreachable) */}
      {error && !items && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !items && !error && (
        <div className="space-y-3 max-w-3xl">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse h-28 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {/* Card list — newest first (server order) */}
      {items && items.length > 0 && (
        <div className="space-y-3 max-w-3xl">
          {items.map((item) => (
            <InboxCard
              key={item.id}
              item={item}
              busy={busyId === item.id}
              onUpdate={onUpdate}
              onReclassify={onReclassify}
              onDismiss={onDismiss}
              onPromote={onPromote}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {items && items.length === 0 && (
        <EmptyState icon={InboxIcon} title={t("emptyTitle")} description={t("emptyDescription")} />
      )}
    </div>
  );
}
